import { useCallback, useEffect, useRef, useState } from "react";

import { SAVED_FILTERS_KEY_PREFIX } from "@/core/constants/keys";
import {
  hasFilterCriteria,
  pruneFilterValue,
  validateSavedFilterName,
} from "../components/filter-panel/filter-panel.model";
import type {
  FilterValue,
  SavedFilter,
  SaveFilterResult,
} from "../components/filter-panel/types-filter-panel";
import {
  MAX_SAVED_FILTERS,
  SAVED_FILTER_LIMIT_MESSAGE,
  SAVED_FILTER_NAME_DUPLICATE_MESSAGE,
  SAVED_FILTER_NAME_MAX_LENGTH,
  SAVED_FILTER_NAME_REQUIRED_MESSAGE,
  SAVED_FILTER_NAME_TOO_LONG_MESSAGE,
} from "../components/filter-panel/messages";

// Mesma API do `useSavedFilters` que o `FilterPanel` do shared tinha antes de sair (Fase 5).
export {
  hasFilterCriteria,
  MAX_SAVED_FILTERS,
  pruneFilterValue,
  SAVED_FILTER_LIMIT_MESSAGE,
  SAVED_FILTER_NAME_DUPLICATE_MESSAGE,
  SAVED_FILTER_NAME_MAX_LENGTH,
  SAVED_FILTER_NAME_REQUIRED_MESSAGE,
  SAVED_FILTER_NAME_TOO_LONG_MESSAGE,
  validateSavedFilterName,
};
export type { SavedFilter, SaveFilterResult };

export const SAVED_FILTER_NO_CRITERIA_MESSAGE =
  "Adicione ao menos um critério para salvar o filtro.";

/** Chave de armazenamento: um conjunto de filtros por tela e por usuário logado. */
export function savedFiltersStorageKey(screen: string, userId: string): string {
  return `${SAVED_FILTERS_KEY_PREFIX}:${screen}:${userId.trim().toLowerCase()}`;
}

function isFilterValue(value: unknown): value is FilterValue {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every(
      (values) => Array.isArray(values) && values.every((item) => typeof item === "string"),
    )
  );
}

function isSavedFilter(item: unknown): item is SavedFilter {
  if (typeof item !== "object" || item === null) return false;

  const { id, name, value } = item as Partial<SavedFilter>;
  return typeof id === "string" && typeof name === "string" && isFilterValue(value);
}

function readSavedFilters(key: string | null): SavedFilter[] {
  if (!key) return [];

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isSavedFilter).slice(0, MAX_SAVED_FILTERS) : [];
  } catch {
    return [];
  }
}

function writeSavedFilters(key: string | null, filters: SavedFilter[]) {
  if (!key) return;

  try {
    localStorage.setItem(key, JSON.stringify(filters));
  } catch {
    // Sem armazenamento (modo privativo, cota cheia): o filtro vale só nesta sessão da tela.
  }
}

let idCounter = 0;

function newId(): string {
  idCounter += 1;
  return `${Date.now().toString(36)}-${idCounter}-${Math.random().toString(36).slice(2, 8)}`;
}

export interface UseSavedFiltersParams {
  /** Identificador da tela na chave de armazenamento (ex.: `"profissionais"`). */
  screen: string;
  /** Id ou e-mail do usuário logado; sem ele os filtros ficam só em memória. */
  userId: string | null | undefined;
}

export interface UseSavedFiltersResult {
  savedFilters: SavedFilter[];
  saveFilter: (name: string, value: FilterValue) => SaveFilterResult;
  deleteFilter: (id: string) => void;
}

/**
 * Filtros salvos de uma tela, só no navegador: ficam no `localStorage` por tela e
 * por usuário logado. Todo acesso ao armazenamento é protegido — se ele falhar, a
 * tela segue funcionando e os filtros valem apenas até recarregar.
 */
export function useSavedFilters({ screen, userId }: UseSavedFiltersParams): UseSavedFiltersResult {
  const key = userId?.trim() ? savedFiltersStorageKey(screen, userId) : null;
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>(() => readSavedFilters(key));
  // Lista mais recente, atualizada no ato: duas chamadas no mesmo ciclo não perdem a primeira.
  const latest = useRef(savedFilters);

  // Troca de usuário na mesma tela carrega os filtros do novo usuário.
  useEffect(() => {
    latest.current = readSavedFilters(key);
    setSavedFilters(latest.current);
  }, [key]);

  const commit = useCallback(
    (next: SavedFilter[]) => {
      latest.current = next;
      setSavedFilters(next);
      writeSavedFilters(key, next);
    },
    [key],
  );

  const saveFilter = useCallback(
    (name: string, value: FilterValue): SaveFilterResult => {
      const error = validateSavedFilterName(name, latest.current);
      if (error) return { ok: false, error };

      const pruned = pruneFilterValue(value);
      if (!hasFilterCriteria(pruned)) return { ok: false, error: SAVED_FILTER_NO_CRITERIA_MESSAGE };

      const id = newId();
      commit([...latest.current, { id, name: name.trim(), value: pruned }]);

      return { ok: true, id };
    },
    [commit],
  );

  const deleteFilter = useCallback(
    (id: string) => commit(latest.current.filter((filter) => filter.id !== id)),
    [commit],
  );

  return { savedFilters, saveFilter, deleteFilter };
}
